import type {
  AttachedScript,
  AttachedScriptSnapshotOut,
  Interactivities,
  InteractivitiesSnapshotOut,
  InteractivityActions,
  InteractivityActionSnapshotOut,
  InteractivityActionsSnapshotOut,
  ScriptConfiguration,
} from '@genially/document-model';
import { InteractivityActionTypeValue } from '@genially/document-model/build/module/interactivity/actions/InteractivityActionTypeValue';
import { containsInteractivityAction } from '@genially/document-model/build/module/interactivity/utils/containsInteractivityAction';
import { walkScriptConfigInteractivityActionTree } from '@genially/document-model/build/module/scripts/walkScriptConfigInteractivityActionTree';

import type { GeniallyContent } from '../../types/GeniallyContent';
import { GeniallyContentItemKey } from '../../types/GeniallyContent';

function isOpenPopupInteractivityAction(
  interactivityAction: InteractivityActionSnapshotOut,
  popupId: string,
) {
  return (
    interactivityAction.type === InteractivityActionTypeValue.SlidePopup &&
    interactivityAction.targetSlideId === popupId
  );
}

function isPopupInsideInteractivities({
  interactivities,
  interactivityActions,
  popupId,
}: {
  interactivities: Interactivities | InteractivitiesSnapshotOut | undefined;
  interactivityActions: InteractivityActions | InteractivityActionsSnapshotOut;
  popupId: string;
}) {
  return containsInteractivityAction({
    interactivities,
    interactivityActions,
    containsFn: ia => isOpenPopupInteractivityAction(ia, popupId),
  });
}

function isPopupInsideScriptConfig({
  scriptConfig,
  popupId,
}: {
  scriptConfig: ScriptConfiguration | undefined;
  popupId: string;
}) {
  let found = false;

  if (scriptConfig) {
    walkScriptConfigInteractivityActionTree(scriptConfig, ia => {
      if (isOpenPopupInteractivityAction(ia, popupId)) {
        found = true;
      }
    });
  }

  return found;
}

type SlideWithPopups = {
  Id: string;
} & (
  | {
      scripts?: { config: ScriptConfiguration }[];
    }
  | {
      data: { scripts?: { config: ScriptConfiguration }[] };
    }
);

const findSlideThatOpensPopup = <S extends SlideWithPopups>(
  slides: S[],
): S | undefined => {
  return slides.find(slide => {
    const data = 'data' in slide ? slide.data : slide;

    if ('scripts' in data && data.scripts) {
      return data.scripts.some(script =>
        isPopupInsideScriptConfig({
          scriptConfig: script.config,
          popupId: slide.Id,
        }),
      );
    }

    return false;
  });
};

type ItemWithPopups =
  | {
      data: {
        IdSlide: string;
        interactivities?: Interactivities;
        script?: AttachedScript;
      };
    }
  | {
      IdSlide: string;
      interactivities?: InteractivitiesSnapshotOut;
      script?: AttachedScriptSnapshotOut;
    };

const findItemThatOpensPopup = <I extends ItemWithPopups>({
  popupId,
  allItems,
  interactivityActions,
}: {
  popupId: string;
  allItems: I[];
  interactivityActions: InteractivityActions | InteractivityActionsSnapshotOut;
}): I | undefined => {
  return allItems.find(item => {
    const data = 'data' in item ? item.data : item;

    return (
      ('interactivities' in data &&
        isPopupInsideInteractivities({
          interactivities: data.interactivities,
          interactivityActions,
          popupId,
        })) ||
      ('script' in data &&
        isPopupInsideScriptConfig({ scriptConfig: data.script?.config, popupId }))
    );
  });
};

export const findPopupParent = <S extends SlideWithPopups, I extends ItemWithPopups>({
  popupId,
  interactivityActions,
  allItems,
  slides,
}: {
  popupId: string;
  interactivityActions: InteractivityActions | InteractivityActionsSnapshotOut;
  allItems: I[];
  slides: S[];
}):
  | { slide: undefined; item: undefined }
  | { slide: S; item: undefined }
  | { slide: S; item: I } => {
  const slideThatOpensPopup = findSlideThatOpensPopup(slides);
  if (slideThatOpensPopup) {
    return { slide: slideThatOpensPopup, item: undefined };
  }

  const itemThatOpensPopup = findItemThatOpensPopup({
    popupId,
    allItems,
    interactivityActions,
  });

  if (!itemThatOpensPopup) {
    return { slide: undefined, item: undefined };
  }

  const parentSlideId =
    'data' in itemThatOpensPopup
      ? itemThatOpensPopup.data.IdSlide
      : itemThatOpensPopup.IdSlide;

  const slide = slides.find(slide => slide.Id === parentSlideId);

  return slide
    ? { slide, item: itemThatOpensPopup }
    : { slide: undefined, item: undefined };
};

export const findPopupParentSlideForGeniallyContent = <S extends SlideWithPopups>(
  popup: S,
  geniallyContent: GeniallyContent,
) => {
  type Dearray<T extends Array<any>> = T extends Array<infer I> ? I : never;
  type ItemType = Dearray<GeniallyContent[GeniallyContentItemKey]>;

  const allItems: ItemType[] = [];
  Object.values(GeniallyContentItemKey).forEach(key => {
    const itemsOfAType = geniallyContent[key];
    if (itemsOfAType) {
      allItems.push(...itemsOfAType);
    }
  });

  return findPopupParent({
    popupId: popup.Id,
    interactivityActions: geniallyContent.interactivityActions,
    allItems,
    slides: geniallyContent.slides,
  }).slide;
};
